import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Spinner } from '../../components/UI';
import { bookingApi, IPublicBooking, isCrmError } from '../../services/crm';
import { browserTimeZone } from '../sales/shared/calendar.helpers';
import { BookingShell } from './components/BookingShell.component';
import { WhenWhere } from './components/WhenWhere.component';

type State = { kind: 'loading' } | { kind: 'done'; booking: IPublicBooking } | { kind: 'off'; message: string; booking: IPublicBooking | null } | { kind: 'missing' };

// #region BookingConfirm
/**
 * /book/confirm/:token: the "Confirm my meeting" button in our emails. Confirms on
 * arrival (it is idempotent, and the token is the only credential), then shows the
 * meeting with ways to join, add it to a calendar or change it.
 */
const BookingConfirm: React.FunctionComponent<{}> = (): React.ReactElement => {
    const { token = '' } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const [state, setState] = React.useState<State>({ kind: 'loading' });
    const timeZone = state.kind === 'done' && state.booking.invitee_time_zone ? state.booking.invitee_time_zone : browserTimeZone();

    React.useEffect(() => {
        document.title = 'Wallmates | Meeting confirmed';
        let live = true;
        (async () => {
            const result = await bookingApi.confirm(token);
            if (!live) return;
            if (!isCrmError(result)) { setState({ kind: 'done', booking: result }); return; }
            if (result.status === 404) { setState({ kind: 'missing' }); return; }
            const current = await bookingApi.manage(token);
            if (live) setState({ kind: 'off', message: result.message, booking: isCrmError(current) ? null : current });
        })();
        return () => { live = false; };
    }, [token]);

    if (state.kind === 'loading') return <BookingShell><div className="flex justify-center px-6 py-16"><Spinner show={true} /></div></BookingShell>;

    if (state.kind === 'missing') {
        return (
            <BookingShell>
                <div className="px-6 py-16 text-center">
                    <h1 className="text-lg font-semibold">We couldn&apos;t find that meeting</h1>
                    <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">The link may be incomplete. Use the button in your email.</p>
                </div>
            </BookingShell>
        );
    }

    if (state.kind === 'off') {
        const b = state.booking;
        return (
            <BookingShell>
                <div className="mx-auto max-w-xl px-6 py-14 text-center">
                    <h1 className="text-xl font-semibold">{b?.status === 'cancelled' ? 'This meeting is no longer on' : 'This meeting can’t be confirmed'}</h1>
                    <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">{b?.status === 'cancelled' ? 'It was cancelled, or released because it wasn’t confirmed in time.' : state.message}</p>
                    {b?.slug ? <div className="mt-6"><Button color="emerald" onClick={() => navigate(`/book/${b.slug}`)}>Pick a new time</Button></div> : null}
                </div>
            </BookingShell>
        );
    }

    const b = state.booking;
    return (
        <BookingShell>
            <div className="mx-auto max-w-xl px-5 py-10 sm:px-8 sm:py-12">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-600/10 text-emerald-700 dark:text-emerald-400" aria-hidden>
                    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
                </div>
                <h1 className="mt-4 text-center text-2xl font-semibold tracking-tight">You&apos;re confirmed</h1>
                <p className="mt-2 text-center text-sm text-zinc-600 dark:text-zinc-400">Thanks, {b.invitee_name.split(/\s+/)[0] || 'there'}. {b.rep_name} will see you then.</p>

                <div className="mt-6 rounded-xl border border-surface-border bg-surface p-4">
                    <WhenWhere title={b.title} startIso={b.start_at} endIso={b.end_at} timeZone={timeZone} meetLink={b.meet_link} />
                </div>

                <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                    {b.meet_link ? <a href={b.meet_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700">Join link</a> : null}
                    <a href={bookingApi.icsUrl(token)} className="text-center text-sm font-medium text-emerald-700 underline underline-offset-2 sm:px-3 dark:text-emerald-400">Add to calendar (.ics)</a>
                    <Link to={`/book/manage/${encodeURIComponent(token)}`} className="text-center text-sm text-zinc-600 underline underline-offset-2 sm:px-3 dark:text-zinc-400">Need to change it?</Link>
                </div>
            </div>
        </BookingShell>
    );
};

export default BookingConfirm;
